import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';

import RoomTypeIncludedGuests from '~/components/Vendors/Properties/RoomTypeIncludedGuests';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { roomRateAvailabilityQueryKeys } from '~/hooks/apis/reservation/roomRateAvailabilityQueryKeys';
import { useGetRoomTypeIncludedGuests } from '~/hooks/apis/reservation/useGetRoomTypeIncludedGuests';

import ReservationService from '~/services/ReservationService';

import { deleteAllInChunks } from '~/utils/deleteAllInChunks';

import { getPartnerSubmittedRoomAsset } from '../../utils';

import MaximumCapacitiesSummary from './MaximumCapacitiesSummary';

interface Props {
  propertyId: string;
  roomType: App.RoomType;
  roomRate: App.RoomRate;
}

export default function IncludedGuestsTab({ propertyId, roomType, roomRate }: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { data: offer } = useGetOffer(offerId);
  const { data: opportunityRelated } = useGetOpportunityRelated(offer?.id_salesforce_external);
  const partnerSubmittedRoomAsset = getPartnerSubmittedRoomAsset(roomType.name, opportunityRelated?.deals);

  const { data: includedGuests = [] } = useGetRoomTypeIncludedGuests(propertyId, roomType.id, roomRate.id);

  function invalidate() {
    queryClient.invalidateQueries({
      queryKey: roomRateAvailabilityQueryKeys.includedGuests(propertyId, roomType.id, roomRate.id),
    });
  }

  async function handleDeleteAll() {
    setIsDeletingAll(true);
    const ids = includedGuests.map((includedGuest) => includedGuest.id);
    await deleteAllInChunks(
      ids,
      (id) => ReservationService.deleteRoomTypeIncludedGuest(id, propertyId, roomType.id, roomRate.id),
      (error) => enqueueSnackbar(`Failed to delete all included guests: ${error.message}`, { variant: 'error' }),
    );
    setIsDeletingAll(false);
    invalidate();
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Combinations default from the unique occupancy codes on this room's deal options — the same basis as Capacity.
        Edit, delete or add rows below.
      </Typography>

      <MaximumCapacitiesSummary roomAsset={partnerSubmittedRoomAsset} offerId={offerId} />

      <RoomTypeIncludedGuests
        propertyId={propertyId}
        roomType={roomType}
        roomRate={roomRate}
        includedGuests={includedGuests}
        deleteRoomTypeIncludedGuest={async (id: string) => {
          await ReservationService.deleteRoomTypeIncludedGuest(id, propertyId, roomType.id, roomRate.id);
          invalidate();
        }}
        onUpdateRoomTypeIncludedGuests={invalidate}
        isDeletingAll={isDeletingAll}
        onDeleteAll={handleDeleteAll}
      />
    </>
  );
}
